import {
  ComponentPropsWithoutRef,
  forwardRef,
  useContext,
  useEffect,
} from "react";
import { StepperContext } from "./Stepper";
import styles from "./StepperPanel.module.scss";
import cx from "classnames";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";
import { Overflow } from "../Overflow";
import { motion } from "motion/react";
import { core } from "@servicetitan/hammer-token";

/**
 * Props for the StepperPanel component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type StepperPanelProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag"
> &
  LayoutUtilProps & {
    /**
     * Unique identifier for the panel.
     * Must match the controls prop of the corresponding Stepper.Step.
     */
    id: string;
  };

/**
 * Content panel component for displaying step content in the Stepper.
 *
 * Features:
 * - Displays content for the current step only
 * - Smooth fade-in/fade-out animations
 * - Automatic overflow handling with scroll support
 * - Accessible with proper ARIA roles
 * - Layout utilities for positioning and spacing
 * - Motion animations for smooth transitions
 * - Focus management for keyboard navigation
 * - Responsive design with flexible layout
 * - Context-based visibility control
 *
 * @example
 * <Stepper.Panel id="step1">
 *   <h2>Personal Information</h2>
 *   <form>
 *     <label htmlFor="name">Name:</label>
 *     <input id="name" type="text" />
 *   </form>
 * </Stepper.Panel>
 *
 * @example
 * <Stepper.Panel id="step2" className="custom-panel">
 *   <div className="step-content">
 *     <h2>Contact Details</h2>
 *     <p>Please provide your contact information.</p>
 *   </div>
 * </Stepper.Panel>
 */
export const StepperPanel = forwardRef<HTMLDivElement, StepperPanelProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const { id, className, style, children, ...rest } = componentProps;
    const { current, items, allCompleted, setCurrentId } =
      useContext(StepperContext);

    const currentId = items?.[current].getAttribute("aria-controls");
    const isCurrent = currentId === id;

    useEffect(() => {
      if (isCurrent && !allCompleted) {
        setCurrentId(currentId ?? "");
      }
    }, [allCompleted, isCurrent, currentId, setCurrentId]);

    const tabPanelClassNames = cx(styles["stepper-panel"], className);
    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    if (isCurrent && !allCompleted) {
      return (
        <motion.div
          style={styleCombined}
          className={tabPanelClassNames}
          transition={{
            opacity: {
              duration:
                +core.primitive.DurationSlow.value.replace("ms", "") / 1000,
            },
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          layout
          {...rest}
          role="tabpanel"
          data-anv="stepper-panel"
          id={id}
          ref={ref}
        >
          <Overflow flexGrow={1}>{children}</Overflow>
        </motion.div>
      );
    }
    return <></>;
  },
);

StepperPanel.displayName = "StepperPanel";
